// =====================================================================
//  Dialogue d'export d'un paquet « easymail-template/1 »
//  ---------------------------------------------------------------------
//  Affiche le paquet produit, avec deux sorties : copier dans le
//  presse-papiers, ou télécharger <nom>.easymail.json. L'option « Inclure
//  les traductions » reconstruit le paquet (par défaut : pivot seul, cf.
//  docs/TEMPLATE_PACKAGE_FORMAT.md § 7).
// =====================================================================

import {IEasymailPackage} from "../../Model/Package/IEasymailPackage.js";
import {PackageMapper} from "../../Tools/Package/PackageMapper.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";
import {Clipboard} from "../../Tools/Clipboard.js";

export interface IPackageExportDialogOptions {
    title: string;
    /** Construit le paquet ; `withTranslations` reflète la case à cocher. */
    build: (withTranslations: boolean) => IEasymailPackage;
    /** Nombre de traductions disponibles (0 : la case n'est pas proposée). */
    translationCount: number;
}

export class PackageExportDialog {
    #options: IPackageExportDialogOptions;
    #kWindow: kendo.ui.Window | undefined;
    #jqText: JQuery | undefined;
    #package: IEasymailPackage | undefined;

    constructor(options: IPackageExportDialogOptions) {
        this.#options = options;
    }

    public static Open = (options: IPackageExportDialogOptions): void => new PackageExportDialog(options).#open();

    #open = () => {
        // Construit d'abord : une erreur (modèle de test invalide…) est notifiée
        // sans ouvrir de fenêtre vide.
        try {
            this.#package = this.#options.build(false);
        } catch (e) {
            NotificationHelper.Instance.error((e as Error).message);
            return;
        }

        this.#kWindow = this.#buildHtml().appendTo(document.body).kendoWindow({
            title: this.#options.title,
            modal: true,
            width: "820px",
            height: "80vh",
            resizable: true,
            actions: ["Close"],
            deactivate: () => this.#kWindow?.destroy(),
        }).data("kendoWindow");
        this.#refresh();
        this.#kWindow.center().open();
    };

    #buildHtml = (): JQuery => {
        const root = $("<div></div>").css({display: "flex", flexDirection: "column", gap: "8px", height: "100%"});

        $("<p></p>").css({margin: 0})
            .text("Donnez ce paquet à votre agent IA (skill easymail-templates) pour qu'il le modifie ou s'en inspire, puis réimportez le résultat.")
            .appendTo(root);

        if (this.#options.translationCount > 0) {
            const label = $("<label></label>").css({display: "flex", gap: "6px", alignItems: "center"}).appendTo(root);
            $("<input type='checkbox' class='k-checkbox k-checkbox-md k-rounded-md'>")
                .on("change", (e) => this.#rebuild((e.target as HTMLInputElement).checked))
                .appendTo(label);
            label.append(document.createTextNode(
                `Inclure les traductions (${this.#options.translationCount}) — inutile si l'agent ne modifie que le français : elles seront régénérées par « Traduire ».`));
        }

        this.#jqText = $("<textarea readonly spellcheck='false'></textarea>")
            .addClass("k-input k-textarea")
            .css({flex: "1 1 auto", fontFamily: "Consolas, monospace", fontSize: "12px", padding: "6px", resize: "none"})
            .appendTo(root);

        const footer = $("<div></div>").css({display: "flex", justifyContent: "flex-end", gap: "8px"}).appendTo(root);
        PackageExportDialog.#button("Copier", "base").on("click", () => this.#copy().then()).appendTo(footer);
        PackageExportDialog.#button("Télécharger", "primary").on("click", () => this.#download()).appendTo(footer);
        PackageExportDialog.#button("Fermer", "base").on("click", () => this.#kWindow?.close()).appendTo(footer);

        return $("<div></div>").append(root);
    };

    #rebuild = (withTranslations: boolean) => {
        try {
            this.#package = this.#options.build(withTranslations);
            this.#refresh();
        } catch (e) {
            NotificationHelper.Instance.error((e as Error).message);
        }
    };

    #refresh = () => this.#jqText.val(PackageMapper.Stringify(this.#package));

    #copy = async () => {
        if (await Clipboard.Copy(PackageMapper.Stringify(this.#package))) {
            NotificationHelper.Instance.success("Paquet copié dans le presse-papiers.");
        } else {
            this.#jqText.trigger("select");
            NotificationHelper.Instance.error("Copie automatique impossible : le texte est sélectionné, faites Ctrl+C.");
        }
    };

    #download = () => {
        const blob = new Blob([PackageMapper.Stringify(this.#package)], {type: "application/json;charset=utf-8"});
        const url = URL.createObjectURL(blob);
        const a = $("<a></a>").attr({href: url, download: PackageMapper.FileName(this.#package)}).appendTo(document.body);
        (a[0] as HTMLAnchorElement).click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    static #button = (text: string, theme: "base" | "primary"): JQuery =>
        $("<button type='button'></button>")
            .addClass(`k-button k-button-md k-rounded-md k-button-solid k-button-solid-${theme}`)
            .text(text);
}
